//LinkedCellsModal.js
import React, { useState, useMemo, useEffect } from 'react';
import './PageLayout.css';
import { linkKey } from '../utils/quantityLinks';

function LinkCellsModal({ isOpen, onClose, onSelectQuantity, lineItems, sectionType, linkedSourceId, excludeIds, currentItemId }) {
  const [selectedItem, setSelectedItem] = useState(null);

  // Ids that must not be offered as a link target: the current item itself, and any
  // item that already depends on it (linking to those would create a circular link).
  const blockedIds = useMemo(() => {
    const set = new Set();
    (excludeIds || []).forEach((id) => set.add(String(id)));
    if (currentItemId != null) set.add(String(currentItemId));
    return set;
  }, [excludeIds, currentItemId]);

   // ✅ NEW: Pre-select the linked item when modal opens
  useEffect(() => {
    if (isOpen && linkedSourceId) {
      const linkedItem = lineItems.find(item => linkKey(item) === String(linkedSourceId));
      
      if (linkedItem) {
        setSelectedItem(linkedItem);
        console.log('✅ Pre-selected linked item:', linkedItem);
      }
    }
  }, [isOpen, linkedSourceId, lineItems]);

  // ✅ Helper function to safely get field value (supports both naming conventions)
  const getField = (item, lowercaseField, uppercaseField) => {
    return item?.[lowercaseField] ?? item?.[uppercaseField];
  };

  // Filter out heading rows and only show items with quantities
  const availableItems = useMemo(() => {
    return lineItems.filter(item => {
      // ✅ Check both uppercase and lowercase for isHeading
      const isHeading = getField(item, 'isHeading', 'IsHeading');

      // ✅ Check both quantity and Factor — allow negatives (e.g. deduction rows),
      // exclude only empty/zero quantities.
      const num = parseFloat(getField(item, 'quantity', 'Factor'));
      const hasQuantity = !isNaN(num) && num !== 0;

      return !isHeading && hasQuantity;
    });
  }, [lineItems]);

  // Selectable = available minus the current item and anything that would form a loop.
  const selectableItems = useMemo(() => {
    return availableItems.filter(item => {
      const itemId = linkKey(item);
      return itemId == null || !blockedIds.has(itemId);
    });
  }, [availableItems, blockedIds]);

  const hiddenToPreventLoop = availableItems.length - selectableItems.length;

  const handleSelect = () => {
    if (selectedItem) {
      // ✅ Handle both field name formats
      const quantity = parseFloat(getField(selectedItem, 'quantity', 'Factor')) || 0;
      console.log('✅ Selected item for linking:', {
        item: selectedItem,
        quantity,
        description: getField(selectedItem, 'description', 'CostMeasurementName')
      });
      onSelectQuantity(quantity, selectedItem);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content link-cells-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Link Cells - {sectionType} Line Items</h3>
        {linkedSourceId && (
          <p className="linked-indicator" style={{ color: '#4A9B8F', fontWeight: 600 }}>
            🔗 Currently linked to an item (highlighted below)
          </p>
        )}
        <p className="modal-description">
          Select a line item to link its Quantity value to the Length column in the calculator
        </p>
        {hiddenToPreventLoop > 0 && (
          <p className="modal-description" style={{ color: '#b45309' }}>
            ⚠️ {hiddenToPreventLoop} item{hiddenToPreventLoop === 1 ? ' is' : 's are'} hidden because
            linking to {hiddenToPreventLoop === 1 ? 'it' : 'them'} would create a circular link.
          </p>
        )}

        <div className="link-cells-table-container">
          <table className="link-cells-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Category</th>
                <th>Description</th>
                <th>Unit</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {selectableItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-items-message">
                    No line items available to link
                  </td>
                </tr>
              ) : (
                selectableItems.map((item, index) => {
                  // ✅ Stable identity via linkKey so selection/highlight matches how links
                  // are stored and resolved (works across both naming conventions).
                  const itemId = linkKey(item);
                  const category = getField(item, 'category', 'Category');
                  const description = getField(item, 'description', 'CostMeasurementName');
                  const unit = getField(item, 'unit', 'QuantityTypeName');
                  const quantity = getField(item, 'quantity', 'Factor');
                  const isSelected = selectedItem != null && linkKey(selectedItem) === itemId;

                  return (
                    <tr
                      key={itemId || index}
                      className={isSelected ? 'selected-row' : ''}
                      onClick={() => setSelectedItem(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="radio-cell">
                        <input
                          type="radio"
                          name="lineItem"
                          checked={isSelected}
                          onChange={() => setSelectedItem(item)}
                        />
                      </td>
                      <td>{category || '—'}</td>
                      <td className="description-cell" title={description}>
                        {description || '—'}
                      </td>
                      <td>{unit || '—'}</td>
                      <td className="quantity-cell">
                        {quantity ? parseFloat(quantity).toFixed(2) : '0.00'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="modal-actions">
          <button 
            className="btn btn--success" 
            onClick={handleSelect}
            disabled={!selectedItem}
          >
            Link Selected Quantity
          </button>
          <button className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default LinkCellsModal;
